import {TweenMax, TimelineMax, Sine, Linear} from 'gsap';
import Utils from '../../../../viscomjs/utils/utils';
import {Factory as GeometryFactory} from './geometry/GeometryFactory';
import {Factory as StatesFactory, ExtractTransitionInitialState} from './state/StatesFactory';
import {prepareRenderer, prepareCamera, prepareScene} from './init/PrepareInfrastructure';
import {prepareMaterial, prepareScreenMaterial, /* prepareScreenLetterMaterial,*/ prepareObject, shuffleTextures} from './init/PrepareObject';

const THREE = require('three');
const EventEmitter = require('eventemitter3');
const SizesUtils = require('../../../../viscomjs/utils/SizesUtils');
// const CustomEase = require('../../../vendors/CustomEase').default;

//

//
// INTERNAL FUNCTIONS:
//
const _mount = (Decoration, el) => {
    el.appendChild(Decoration.domElement);
};

const _remove = Decoration => {
    if (!Decoration.domElement || !Decoration.domElement.parentNode) return;
    Decoration.domElement.parentNode.removeChild(Decoration.domElement);
};

const _degToRad = deg => (Math.PI * deg) / 180;

const _construct = Decoration => {
    //
    // 1. Prepare Infrastructure
    const obj = prepareCamera({aspect: Decoration.width / Decoration.height});
    Decoration.camera = obj.camera;
    Decoration.cameraRigRoot = obj.cameraRigRoot;
    Decoration.cameraRigCurve = obj.cameraRigCurve;
    Decoration.cameraRigNull = obj.cameraRigNull;
    // Point of interest to animate in front of camera:
    Decoration.cameraRigPOI = obj.cameraRigPOI;
    Decoration.renderer = prepareRenderer(Decoration.width, Decoration.height);
    Decoration.rendererAnisotropy = Decoration.renderer.capabilities.getMaxAnisotropy() || 1;
    Decoration.scene = prepareScene();

    // 2. Prepare Secondary Objects:
    // - root to change a total composition position relative to the point of interest
    Decoration.root = new THREE.Group();
    Decoration.rootParent = new THREE.Group();

    // - nullLevel0 to move blocks as a group
    Decoration.nullLevel0 = new THREE.Group();

    // - Root of all the tunnel objects to add
    Decoration.blocksRoot = new THREE.Group();

    Decoration.blockGroups = {};
    Decoration.screenGroups = {};
    Decoration.locationGroups = {};
    Decoration.transitionGroups = {};

    Decoration.nullLevel0.add(Decoration.blocksRoot);
    Decoration.root.add(Decoration.nullLevel0);
    Decoration.rootParent.add(Decoration.root);
    Decoration.scene.add(Decoration.rootParent);
    Decoration.scene.add(Decoration.cameraRigRoot);
    Decoration.scene.add(Decoration.cameraRigPOI);
    Decoration.scene.add(Decoration.camera);

    Decoration.domElement = Decoration.renderer.domElement;

    // Decoration.statesFactory = StatesFactory(Decoration.params, Decoration.logoGeometryStore);
};

const _addGeometry = (Decoration, geometry, hasTransition) => {
    //
    // 3. Prepare View:
    // 3.1. Prepare Geometries:
    // - geometry to be rendered
    if (!geometry || !geometry.geometry) return;

    // 3.2. Block parts parent group:
    const blockName = geometry.name;
    const blockGroup = Decoration.blockGroups[blockName] || new THREE.Group();

    // 3.3. Create only if there's none
    if (!Decoration.screenGroups[blockName] && geometry.geometry.screen) {
        const screenGroup = new THREE.Group();
        blockGroup.add(screenGroup);
        Decoration.screenGroups[blockName] = screenGroup;

        _addScreenBlocks(Decoration, geometry.geometry.screen, screenGroup, blockName);
    }

    if (!Decoration.locationGroups[blockName] && geometry.geometry.location) {
        const locationGroup = new THREE.Group();
        blockGroup.add(locationGroup);
        Decoration.locationGroups[blockName] = locationGroup;

        _addBlocks(Decoration, geometry.geometry.location, locationGroup, true);
    }

    if (hasTransition && !Decoration.transitionGroups[blockName] && geometry.geometry.transition) {
        const transitionGroup = new THREE.Group();
        blockGroup.add(transitionGroup);
        Decoration.transitionGroups[blockName] = transitionGroup;

        _addBlocks(Decoration, geometry.geometry.transition, transitionGroup, false);
    }

    if (!Decoration.blockGroups[blockName]) {
        blockGroup.position.copy(geometry.position);
        blockGroup.rotation.copy(geometry.rotation);
        Decoration.blockGroups[blockName] = blockGroup;
        Decoration.blocksRoot.add(blockGroup);
    }
};

const _addScreenBlocks = (Decoration, geometry, blockGroup, stateName) => {
    //
    geometry.forEach(g => {
        // 3.3.1. Prepare Material
        const material1 = prepareScreenMaterial(stateName, Decoration.rendererAnisotropy);
        // const material2 = prepareScreenLetterMaterial(stateName, Decoration.rendererAnisotropy);

        // 3.3.2. Prepare View Objects:
        // - points to draw the main geometry
        blockGroup.add(prepareObject(g, material1));

        /* const f = g.faces[0];
        if (f && f.normal) {
            const ltr = prepareObject(g, material2);
            ltr.position.add(f.normal);console.log(f.normal, ltr.position)
            blockGroup.add(ltr);
        }*/
    });
};

const _addBlocks = (Decoration, geometry, blockGroup, isUnique) => {
    //
    if (isUnique) shuffleTextures();

    //
    geometry.forEach(g => {
        // 3.3.1. Prepare Material
        const material = prepareMaterial(Decoration.rendererAnisotropy, isUnique);

        // 3.3.2. Prepare View Objects:
        // - points to draw the main geometry
        blockGroup.add(prepareObject(g, material));
    });
};

const _clearGeometry = (Decoration, exceptBlockName) => {
    // 4. Remove unnecessary blocks
    let k;
    let obj;

    // 4.1. Remove all the transition objects
    for (k in Decoration.transitionGroups) {
        if (!Decoration.transitionGroups.hasOwnProperty(k)) continue;
        obj = Decoration.transitionGroups[k];
        if (obj.parent) obj.parent.remove(obj);
        delete Decoration.transitionGroups[k];
    }

    // 4.2. Remove not current screen objects
    for (k in Decoration.screenGroups) {
        if (!Decoration.screenGroups.hasOwnProperty(k) || k === exceptBlockName) continue;
        obj = Decoration.screenGroups[k];
        if (obj.parent) obj.parent.remove(obj);
        delete Decoration.screenGroups[k];
    }

    // 4.3. Remove not current location objects
    for (k in Decoration.locationGroups) {
        if (!Decoration.locationGroups.hasOwnProperty(k) || k === exceptBlockName) continue;
        obj = Decoration.locationGroups[k];
        if (obj.parent) obj.parent.remove(obj);
        delete Decoration.locationGroups[k];
    }
};

const _showScreen = Decoration => {
    if (Decoration._scrTl) {
        Decoration._scrTl.kill();
        Decoration._scrTl = null;
    }

    let k;
    let obj;
    const tl = new TimelineMax({
        delay: 0.2,
        onUpdate: d => {
            d.render();
        },
        onUpdateParams: [Decoration],
        onComplete: d => {
            if (d._scrTl) {
                d._scrTl.kill();
                d._scrTl = null;
            }
        },
        onCompleteParams: [Decoration]
    });

    for (k in Decoration.screenGroups) {
        if (!Decoration.screenGroups.hasOwnProperty(k)) continue;
        obj = Decoration.screenGroups[k];

        obj.children.forEach(ch => {
            tl.to(
                ch.material,
                0.35,
                {
                    opacity: 1,
                    onUpdate: el => {
                        el.needUpdate = true;
                    },
                    onUpdateParams: [ch.material]
                },
                'st'
            );
        });
    }

    Decoration._scrTl = tl;
};

const _hideScreen = Decoration => {
    if (Decoration._scrTl) {
        Decoration._scrTl.kill();
        Decoration._scrTl = null;
    }

    let k;
    let obj;
    const tl = new TimelineMax({
        onUpdate: d => {
            d.render();
        },
        onUpdateParams: [Decoration],
        onComplete: d => {
            if (d._scrTl) {
                d._scrTl.kill();
                d._scrTl = null;
            }
        },
        onCompleteParams: [Decoration]
    });

    for (k in Decoration.screenGroups) {
        if (!Decoration.screenGroups.hasOwnProperty(k)) continue;
        obj = Decoration.screenGroups[k];

        obj.children.forEach(ch => {
            tl.to(
                ch.material,
                0.5,
                {
                    opacity: 0,
                    onUpdate: el => {
                        el.needUpdate = true;
                    },
                    onUpdateParams: [ch.material]
                },
                'st'
            );
        });
    }

    Decoration._scrTl = tl;
};

const _applyObjPosition = (to, from) => {
    to.position.x = from.x;
    to.position.y = from.y;
    to.position.z = from.z;
};

const _getCameraObjPosition = cameraRigNull => {
    const mx = cameraRigNull.matrixWorld.elements;
    return {x: mx[12], y: mx[13], z: mx[14]};
};

const _forceInitialRender = Decoration => {
    const f = () => {
        Decoration._needForceInvalidate = true;
        Decoration.render();
    };

    TweenMax.delayedCall(0.1, f);
    TweenMax.delayedCall(0.2, f);
};

//

//
// INTERNAL METHODS FUNCTIONS:
//
const _onResize = function() {
    this.width = SizesUtils.winWidth();
    this.height = SizesUtils.winHeight();

    if (this.height < 1 || this.width < 1) return;

    // Camera frustum vertical field of view, from bottom to top of view, in degrees:

    if (this.width > this.height) {
        this.camera.fov = (this.state.camera.fovRatio * this.height) / Math.min(this.width, this.height);
    } else {
        this.camera.fov = (this.state.camera.fovRatio * 0.9 * this.height) / Math.min(this.width, this.height);
    }
    this.camera.aspect = this.width / this.height;

    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this.width, this.height);
    this.render();
};

// _invalidate: calculate geometry and camera configuration
//
const _invalidate = function() {
    let cameraPositionNeedsUpdate = this._stateApplied || this._cameraPositionChanged || this._needForceInvalidate;

    // WATCH GEOMETRY / ROUND
    // this.root.rotation.y += _degToRad(0.6);
    // this.root.rotation.x += _degToRad(0.2);

    // CAMERA: POSITION etc.
    if (this._cameraPositionChanged) {
        if (this.state.camera.positionCurve /* && this.state.camera.pOICurve*/) {
            //
            this.state.camera.position = this.state.camera.positionCurve.getPoint(this.state.camera.transitionRatio, this.state.camera.position);
            // this.state.camera.pOIPosition = this.state.camera.pOICurve.getPoint(this.state.camera.transitionRatio, this.state.camera.pOIPosition);
            // console.log(this.state.camera.positionCurve.getLength(), this.state.camera.positionCurve.arcLengthDivisions, this.state.camera.transitionRatio, this.state.camera.position, this.state.camera.pOIPosition)
        }

        if (this.state.camera.rotationTween) {
            this.state.camera.rotationTween.timeline.progress(this.state.camera.transitionRatio);
            this.state.camera.rotation.x = this.state.camera.rotationTween.object.x;
            this.state.camera.rotation.y = this.state.camera.rotationTween.object.y;
            this.state.camera.rotation.z = this.state.camera.rotationTween.object.z;
        }

        if (this.state.camera.addRotationZTween) {
            this.state.camera.addRotationZTween.timeline.progress(this.state.camera.transitionRatio);

            this.state.camera.rotation.dz = this.state.camera.addRotationZTween.object.z;
        } else {
            this.state.camera.rotation.dz = 0;
        }

        cameraPositionNeedsUpdate = true;
        this._cameraPositionChanged = false;
    }

    if (this._cameraRotationChanged || this._stateApplied) {
        // ??
        this._cameraRotationChanged = false;
    }

    if (cameraPositionNeedsUpdate && this.state.camera.position) {
        this.cameraRigCurve.position.copy(this.state.camera.position);
        // this.cameraRigPOI.position.copy(this.state.camera.pOIPosition);
    }

    if (cameraPositionNeedsUpdate || this._needForceCameraPositionUpdate) _applyObjPosition(this.camera, _getCameraObjPosition(this.cameraRigNull));

    //
    this._needForceInvalidate = false;
    this._stateApplied = false;

    // To force render camera rigs be applied
    this._needForceCameraPositionUpdate = cameraPositionNeedsUpdate;
};

// Apply state immediately
//
const _applyState = function(state) {
    if (!state) return;

    this.state = _extendState(this.state, state);

    // UPDATE BLOCKS CONFIGURATION:
    _addGeometry(this, state.geometry, false);
    _clearGeometry(this, state.geometry ? state.geometry.name : '');
    // console.log('_clearGeometry except', state.geometry ? state.geometry.name : '')
    /*
    this.state.geometry.invalidate = () => {

    };
    this.state.textures...;
    */

    this.state.camera.transitionRatio = this.state.transitionIsForth ? 1 : 0;

    this._stateApplied = true;
};

const _extendState = function() {
    const args = [];
    let len$1 = arguments.length;
    while (len$1--) args[len$1] = arguments[len$1];

    const to = Object(args[0]);
    for (let i = 1; i < args.length; i += 1) {
        const nextSource = args[i];
        if (nextSource !== undefined && nextSource !== null) {
            const keysArray = Object.keys(Object(nextSource));
            for (let nextIndex = 0, len = keysArray.length; nextIndex < len; nextIndex += 1) {
                const nextKey = keysArray[nextIndex];
                const desc = Object.getOwnPropertyDescriptor(nextSource, nextKey);
                if (desc !== undefined && desc.enumerable) {
                    if (to[nextKey] instanceof THREE.Vector3 && nextSource[nextKey] instanceof THREE.Vector3) {
                        to[nextKey] = to[nextKey].copy(nextSource[nextKey]);
                    } else if (!(to[nextKey] && to[nextKey] instanceof THREE.Vector3) && nextSource[nextKey] instanceof THREE.Vector3) {
                        to[nextKey] = nextSource[nextKey].clone();
                    } else if (to[nextKey] instanceof THREE.Geometry && nextSource[nextKey] instanceof THREE.Geometry) {
                        to[nextKey] = nextSource[nextKey];
                    } else if (!(to[nextKey] && to[nextKey] instanceof THREE.Geometry) && nextSource[nextKey] instanceof THREE.Geometry) {
                        to[nextKey] = nextSource[nextKey];
                    } else if (Utils.isObject(to[nextKey]) && Utils.isObject(nextSource[nextKey])) {
                        _extendState(to[nextKey], nextSource[nextKey]);
                    } else if (!Utils.isObject(to[nextKey]) && Utils.isObject(nextSource[nextKey])) {
                        to[nextKey] = {};
                        _extendState(to[nextKey], nextSource[nextKey]);
                    } else {
                        to[nextKey] = nextSource[nextKey];
                    }
                }
            }
        }
    }
    return to;
};

// _render: render only
//
const _render = function() {
    //
    // Reset z rotation to exclude unfounded camera behavior
    this.camera.rotation.x = _degToRad(this.state.camera.rotation.x);
    this.camera.rotation.y = _degToRad(this.state.camera.rotation.y);
    this.camera.rotation.z = _degToRad(this.state.camera.rotation.z + this.state.camera.rotation.dz);

    this.renderer.render(this.scene, this.camera);
};

const _onStartTransition = Decoration => {
    Decoration.transition = true;
    Decoration.emit('startTransition');
};

const _onStopTransition = Decoration => {
    Decoration._tl = null;
    Decoration.transition = false;
    Decoration.emit('endTransition');
};

const _onCuePoint = (Decoration, name) => {
    Decoration.emit('cuePoint', name);
};

const _get2DCoordinates = function(point, target) {
    const v = this._getPointWorldCoordinates(point).project(this.camera);
    target.setX((v.x + 1) * this.width * 0.5);
    target.setY(-(v.y - 1) * this.height * 0.5);
    return target;
};

const _getPointWorldCoordinates = function(point) {
    return this.root.localToWorld(point.clone());
};

//

//
// INTERFACE METHODS FUNCTIONS:
//
const init = (Decoration, el) => {
    if (Decoration.initiated) return Decoration;

    // Initial size
    Decoration.width = SizesUtils.winWidth();
    Decoration.height = SizesUtils.winHeight();

    _construct(Decoration);

    // SET INTERNAL METHODS:
    Decoration._onResize = _onResize.bind(Decoration);
    Decoration._invalidate = _invalidate.bind(Decoration);
    Decoration._applyState = _applyState.bind(Decoration);
    Decoration._render = _render.bind(Decoration);
    Decoration._get2DCoordinates = _get2DCoordinates.bind(Decoration);
    Decoration._getPointWorldCoordinates = _getPointWorldCoordinates.bind(Decoration);

    Decoration._applyState(Decoration.statesFactory(Decoration.stateName));
    Decoration._onResize();

    // Add listeners
    Decoration.win.addEventListener('resize', Decoration._onResize);

    // Mount
    Decoration.el = el;
    _mount(Decoration, el);
    _forceInitialRender(Decoration);

    Decoration.initiated = true;

    return Decoration;
};

const destroy = Decoration => {
    if (!Decoration.initiated) return Decoration;

    Decoration.stop();

    Decoration.removeAllListeners();

    Decoration.win.removeEventListener('resize', Decoration._onResize);
    _remove(Decoration);

    delete Decoration.renderer;
    delete Decoration.camera;
    delete Decoration.scene;
    delete Decoration.domElement;
    delete Decoration.geometries;
    delete Decoration.screenGroups;
    delete Decoration.locationGroups;
    delete Decoration.transitionGroups;

    delete Decoration._onResize;
    delete Decoration._invalidate;
    delete Decoration._applyState;
    delete Decoration._render;

    Decoration.initiated = false;

    return Decoration;
};

const start = Decoration => {
    if (!Decoration.initiated || Decoration.started) return Decoration;

    Decoration._needForceInvalidate = true;
    Decoration.started = true;
    Decoration._dt = 0;
    Decoration.render();
    // Decoration.ticker.addEventListener('tick', Decoration.render);

    return Decoration;
};

const stop = Decoration => {
    if (!Decoration.initiated || !Decoration.started) return Decoration;

    Decoration.started = false;
    Decoration._dt = 0;
    // Decoration.ticker.removeEventListener('tick', Decoration.render);

    return Decoration;
};

const render = (Decoration, dt = 0) => {
    if (!Decoration.initiated) return Decoration;
    /* Decoration._dt += dt;
    if (Decoration._dt < 1 / (Decoration.params.fps + 5)) return;
    Decoration._dt = 0;*/
    Decoration._invalidate(dt);
    Decoration._render();
    return Decoration;
};

const setState = function(stateName, needCompleteTransition, needTransition) {
    if (this.stateName === stateName) return this;

    this.prevStateName = this.stateName;
    this.stateName = stateName;

    // console.log(this.prevStateName, 'to', stateName, needTransition);

    if (!this.initiated) return this;

    const newState = needCompleteTransition && needTransition ? this.statesFactory(this.stateName, this.prevStateName) : this.statesFactory(this.stateName);

    if (this._tl) {
        this._tl.kill();
        this._tl = null;
    }

    if (needTransition && this.started) {
        // SET UP TRANSITION INITIAL STATE:
        const prevState = ExtractTransitionInitialState(newState);
        this._applyState(prevState);
        //
    } else {
        // SET UP STATE WITH NO TRANSITION:
        _onStartTransition(this);
        this._applyState(newState);
        _onStopTransition(this);
        return;
    }

    // Add new geometry blocks:
    _addGeometry(this, newState.geometry, newState.transitionIsForth);

    // Add previous transition geometry of the previous block
    if (!newState.transitionIsForth && newState.previousGeometry) {
        _addGeometry(this, newState.previousGeometry, true);
    }

    // ANIMATE:

    // TweenMax.ticker.fps(Decoration.params.fps);

    const tl = new TimelineMax({
        onUpdate: d => {
            d.render();
        },
        onUpdateParams: [this],
        onComplete: d => {
            d._applyState(newState);
            _onStopTransition(d);
            d._tl = null;
        },
        onCompleteParams: [this]
    });

    // 1. CAMERA
    this.state.camera.transitionRatio = newState.transitionIsForth ? 0 : 1;
    this.state.camera.positionCurve = newState.camera.positionCurve;
    // this.state.camera.pOICurve = newState.camera.pOICurve;
    this.state.camera.rotationTween = newState.camera.rotationTween;
    this.state.camera.addRotationZTween = newState.camera.addRotationZTween;
    this.state.camera.speed = newState.camera.speed;

    //
    if (this.state.camera.positionCurve) {
        //
        // const speed = 10.8;
        const speed = this.state.camera.speed || 6;
        const L = this.state.camera.positionCurve.getLength();
        let inL = 500;
        let outL = 700;

        inL = Math.min(inL / L, 0.1);
        outL = Math.min(outL / L, 0.12);
        const innerL = 1 - inL - outL;
        tl.to(
            this.state.camera,
            inL * 1.35 * speed,
            {
                transitionRatio: newState.transitionIsForth ? inL : 1 - inL,
                onUpdate: () => {
                    this._cameraPositionChanged = true;
                },
                ease: Sine.easeIn
            },
            'st'
        );
        tl.to(
            this.state.camera,
            innerL * speed,
            {
                transitionRatio: newState.transitionIsForth ? inL + innerL : 1 - inL - innerL,
                onUpdate: () => {
                    this._cameraPositionChanged = true;
                },
                ease: Linear.easeInOut
            },
            'st+=' + inL * 0.95 * speed
        );
        tl.to(
            this.state.camera,
            outL * 1.35 * speed,
            {
                transitionRatio: newState.transitionIsForth ? 1 : 0,
                onUpdate: () => {
                    this._cameraPositionChanged = true;
                },
                ease: Sine.easeOut
            },
            'st+=' + (inL * 0.95 + innerL) * speed
        );

        // ZOOM-IN CUE POINT
        tl.addLabel('end');
        tl.call(_onCuePoint, [this, 'zoomIn'], null, 'end-=' + this.params.zoomInCuePoint);
    } else {
        tl.set(
            this.state.camera.position,
            {
                x: newState.camera.position.x,
                y: newState.camera.position.y,
                z: newState.camera.position.z,
                onUpdate: () => {
                    this._cameraPositionChanged = true;
                }
            },
            'st'
        );

        tl.call(_onCuePoint, [this, 'zoomIn']);
    }

    _onStartTransition(this);

    this._tl = tl;

    return this;
};

//

//
// OBJECT CONSTRUCTOR:
//
const Tunnel3D = (params = {}) => {
    let Decoration = new EventEmitter();

    const defaults = {
        geometryScale: 1,
        camera: {
            pointOfInterestZ0: 100
        },
        zoomInCuePoint: 1
    };

    const params2 = Utils.extend(defaults, params);

    const initialProps = {
        initiated: false,
        win: window,
        el: null,
        geometryFactory: GeometryFactory(),
        statesFactory: StatesFactory(params2, GeometryFactory()),
        ticker: TweenMax.ticker,
        time: TweenMax.ticker.time,
        prevStateName: 'initial',
        stateName: 'initial',
        state: {
            camera: {
                fovRatio: params2.camera.fovRatio,
                transitionRatio: 1,
                position: new THREE.Vector3(),
                // pOIPosition: new THREE.Vector3(),
                normal: new THREE.Vector3(),
                rotation: {
                    x: 0,
                    y: 0,
                    z: 0,
                    dz: 0
                }
            }
        },
        params: params2
    };

    Decoration = Utils.extend(Decoration, initialProps);

    Decoration.ticker.fps(Decoration.params.fps);

    // INTERFACE METHODS:
    Decoration.init = el => init(Decoration, el);
    Decoration.destroy = () => destroy(Decoration);
    Decoration.start = () => start(Decoration);
    Decoration.stop = () => stop(Decoration);
    Decoration.render = () => {
        const t = Decoration.ticker.time;
        const dt = t - Decoration.time;
        if (!isNaN(dt) && dt > 0) render(Decoration, dt);
        Decoration.time = t;
        return Decoration;
    };
    Decoration.setState = setState.bind(Decoration);
    Decoration.showScreen = () => {
        _showScreen(Decoration);
    };
    Decoration.hideScreen = () => {
        _hideScreen(Decoration);
    };

    return Decoration;
};

export {Tunnel3D};
export default Tunnel3D;
